// components/ClientesTab.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import ClientModal from './clients/ClientModal';
import ClientsTable from './clients/ClientsTable';
import DeleteConfirmationDialog from './clients/DeleteConfirmationDialog';
import { axiosInstance } from '../utils/axios';


const ClientesTab = () => {
  // Estados principales
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Cargar clientes al montar el componente
  useEffect(() => {
    fetchClients();
  }, []);

  // Función para obtener la lista de clientes
  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/clientes');
      setClients(response.data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      showSnackbar('Error al cargar la lista de clientes', 'error');
      
      // Si es un error de conexión, usar datos de ejemplo
      if (error.code === 'NETWORK_ERROR' || error.response?.status >= 500) {
        setClients([
          {
            id: 1,
            razon_social: 'Empresa Demo S.A.',
            horas: 160,
            monto_base: 150000
          },
          {
            id: 2,
            razon_social: 'Tecnología ABC',
            horas: 80,
            monto_base: 85000
          }
        ]);
        showSnackbar('Mostrando datos de ejemplo (sin conexión a API)', 'warning');
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para mostrar notificaciones
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Función para crear o editar cliente
  const handleSaveClient = async (clientData) => {
    setActionLoading(true);
    try {
      if (editingClient) {
        // Editar cliente existente
        const response = await axiosInstance.put(`/cliente/${editingClient.id}`, clientData);
        setClients(prev => prev.map(client => 
          client.id === editingClient.id ? response.data : client
        ));
        showSnackbar('Cliente actualizado exitosamente');
      } else {
        // Crear nuevo cliente
        const response = await axiosInstance.post('/cliente', clientData);
        setClients(prev => [...prev, response.data]);
        showSnackbar('Cliente creado exitosamente');
      }
      
      // Cerrar modal y limpiar estado
      setModalOpen(false);
      setEditingClient(null);
    } catch (error) {
      console.error('Error al guardar cliente:', error);
      
      // Manejo de errores específicos
      if (error.response?.status === 400) {
        showSnackbar('Datos inválidos. Por favor verifique la información', 'error');
      } else if (error.response?.status === 409) {
        showSnackbar('Ya existe un cliente con esa razón social', 'error');
      } else {
        showSnackbar('Error al guardar el cliente', 'error');
        
        // En caso de error de conexión, simular éxito localmente
        if (error.code === 'NETWORK_ERROR') {
          const mockClient = {
            id: Date.now(), // ID temporal
            ...clientData
          };
          
          if (editingClient) {
            setClients(prev => prev.map(client => 
              client.id === editingClient.id ? { ...editingClient, ...clientData } : client
            ));
          } else {
            setClients(prev => [...prev, mockClient]);
          }
          
          setModalOpen(false);
          setEditingClient(null);
          showSnackbar('Cliente guardado localmente (sin conexión a API)', 'warning');
        }
      }
    } finally {
      setActionLoading(false);
      fetchClients(); // Refrescar lista de clientes
    }
  };

  // Función para abrir modal de edición
  const handleEditClient = (client) => {
    setEditingClient(client);
    setModalOpen(true);
  };

  // Función para confirmar eliminación
  const handleDeleteClient = (client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  // Función para ejecutar eliminación
  const confirmDeleteClient = async (client) => {
    setActionLoading(true);
    try {
      await axiosInstance.delete(`/clientes/${client.id}`);
      setClients(prev => prev.filter(c => c.id !== client.id));
      showSnackbar('Cliente eliminado exitosamente');
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    } catch (error) {
      console.error('Error al eliminar cliente:', error);
      
      if (error.response?.status === 404) {
        showSnackbar('El cliente ya no existe', 'error');
      } else if (error.response?.status === 409) {
        showSnackbar('No se puede eliminar: el cliente tiene documentos asociados', 'error');
      } else {
        showSnackbar('Error al eliminar el cliente', 'error');
        
        // En caso de error de conexión, simular eliminación local
        if (error.code === 'NETWORK_ERROR') {
          setClients(prev => prev.filter(c => c.id !== client.id));
          setDeleteDialogOpen(false);
          setClientToDelete(null);
          showSnackbar('Cliente eliminado localmente (sin conexión a API)', 'warning');
        }
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Función para abrir modal de creación
  const handleAddClient = () => {
    setEditingClient(null);
    setModalOpen(true);
  };

  // Función para cerrar modal
  const handleCloseModal = () => {
    if (!actionLoading) {
      setModalOpen(false);
      setEditingClient(null);
    }
  };

  // Función para cerrar diálogo de eliminación
  const handleCloseDeleteDialog = () => {
    if (!actionLoading) {
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    }
  };

  return (
    <Container maxWidth="lg">
      {/* Encabezado */}
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <div>
            <Typography variant="h4" gutterBottom>
              👥 Gestión de Clientes
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Administra la información de tus clientes y sus servicios
            </Typography>
          </div>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
              onClick={fetchClients}
              disabled={loading}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddClient}
              size="large"
            >
              Agregar Cliente
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabla de clientes */}
      <Box sx={{ mt: 3 }}>
        <ClientsTable
          clients={clients}
          onEdit={handleEditClient}
          onDelete={handleDeleteClient}
          loading={loading}
        />
      </Box>

      {/* Modal para crear/editar cliente */}
      <ClientModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveClient}
        client={editingClient}
        loading={actionLoading}
      />

      {/* Diálogo de confirmación para eliminar */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={confirmDeleteClient}
        client={clientToDelete}
        loading={actionLoading}
      />

      {/* Notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ClientesTab;